import { Button } from "@/components/ui/button";
import { MapPin, ArrowRight, Ticket } from "lucide-react";
import heroDesktop from "@/assets/uploads/hero-desktop.webp.asset.json";
import heroMobile from "@/assets/uploads/hero-mobile.webp.asset.json";
import { useI18n } from "@/i18n/I18nProvider";
import { useTicketAnchorProps } from "./TicketsProvider";
import { useMediaSlot } from "./PageMediaProvider";

export function Hero() {
  const { t } = useI18n();
  const ticketAnchor = useTicketAnchorProps();
  const hero = useMediaSlot("hero", { desktop: heroDesktop.url, mobile: heroMobile.url });
  const overlay = hero.slot?.overlayOpacity ?? 1;
  const glow = hero.slot?.glowOpacity ?? 1;
  return (
    <section id="top" className="film-grain relative isolate min-h-[100svh] overflow-hidden">
      <picture>
        <source media="(max-width: 767px)" srcSet={hero.mobile} />
        <source media="(min-width: 768px)" srcSet={hero.desktop} />
        <img
          src={hero.desktop}
          alt={hero.alt || t.hero.heroAlt}
          width={1920}
          height={1280}
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 -z-20 h-full w-full scale-105 object-cover md:cinema-flicker"
        />
      </picture>
      {/* Layered cinematic vignette */}
      <div
        className="absolute inset-0 -z-10 bg-gradient-to-b from-night-deep/65 via-night-deep/55 to-night-deep"
        style={{ opacity: overlay }}
      />
      <div
        className="absolute inset-0 -z-10 bg-[radial-gradient(120%_70%_at_50%_120%,oklch(0.86_0.16_72/0.18),transparent_60%)]"
        style={{ opacity: glow }}
      />
      <div
        className="absolute inset-x-0 top-0 -z-10 h-40 bg-gradient-to-b from-night-deep/95 to-transparent"
        style={{ opacity: overlay }}
      />
      <div
        className="absolute inset-y-0 left-0 -z-10 w-1/3 bg-gradient-to-r from-night-deep/70 to-transparent"
        style={{ opacity: overlay }}
      />

      <div className="relative mx-auto flex min-h-[100svh] max-w-7xl flex-col justify-end px-5 pb-28 pt-32 md:px-8 md:pb-36 md:pt-44">
        <div className="max-w-4xl">
          <div className="mb-7 inline-flex items-center gap-2.5 rounded-full border border-gold/30 bg-night-deep/50 px-3.5 py-1.5 font-mono-cinema text-[10px] text-gold backdrop-blur-md uppercase tracking-wider">
            <span className="h-1.5 w-1.5 rounded-full bg-gold shrink-0 md:animate-pulse" />
            {t.hero.badge}
          </div>

          <h1 className="font-display text-balance text-[2.85rem] font-medium leading-[0.92] tracking-tight sm:text-6xl md:text-[5.25rem] lg:text-[6.5rem]">
            {t.hero.title1}
            <span className="mt-1 block italic font-normal bg-gradient-to-r from-amber-glow via-gold to-amber-glow bg-clip-text text-transparent">
              {t.hero.title2}
            </span>
            <span className="mt-1 block text-foreground/95">{t.hero.title3}</span>
          </h1>

          <p className="mt-7 max-w-xl text-balance text-base leading-relaxed text-foreground/80 md:text-lg">
            {t.hero.intro}
          </p>

          {/* Editorial date strip */}
          <div className="mt-10 flex flex-wrap items-stretch gap-x-8 gap-y-5 border-y border-border/70 py-5">
            <div>
              <div className="font-mono-cinema text-[10px] text-foreground/75">{t.hero.dates}</div>
              <div className="mt-1 font-display text-2xl leading-none md:text-3xl">
                {t.hero.datesValue}
              </div>
            </div>
            <div className="hidden w-px bg-border sm:block" />
            <div>
              <div className="font-mono-cinema text-[10px] text-foreground/75">{t.hero.location}</div>
              <div className="mt-1 flex items-center gap-1.5 font-display text-lg md:text-xl">
                <MapPin className="h-4 w-4 text-gold" />
                {t.hero.locationValue}
              </div>
            </div>
            <div className="hidden w-px bg-border sm:block" />
            <div>
              <div className="font-mono-cinema text-[10px] text-foreground/75">{t.hero.capacity}</div>
              <div className="mt-1 font-display text-lg md:text-xl">
                {t.hero.capacityOutside} · <span className="text-foreground/75">{t.hero.capacityCovered}</span>
              </div>
            </div>
            <div className="hidden w-px bg-border sm:block" />
            <div>
              <div className="font-mono-cinema text-[10px] text-gold">{t.hero.startLabel}</div>
              <div className="mt-1 font-display text-lg md:text-xl">{t.hero.startValue}</div>
              <div className="mt-0.5 font-mono-cinema text-[10px] text-foreground/75">{t.hero.startSub}</div>
            </div>
          </div>

          <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center">
            {ticketAnchor && (
              <Button asChild variant="hero" size="xl">
                <a {...ticketAnchor}>
                  <Ticket className="h-4 w-4" /> {t.common.buyTickets}
                </a>
              </Button>
            )}
            <Button asChild variant="ghostLine" size="xl">
              <a href="#programma">{t.common.viewProgram} <ArrowRight className="h-4 w-4" /></a>
            </Button>
            <span className="ml-1 hidden font-mono-cinema text-[10px] text-foreground/75 sm:inline">
              {t.hero.note}
            </span>
          </div>
        </div>
      </div>

      {/* Bottom marquee — cinematic ticker */}
      {hero.credit && (
        <div className="absolute bottom-16 right-5 z-10 font-mono-cinema text-[10px] uppercase tracking-wider text-foreground/50 md:bottom-14 md:right-8">
          Foto: {hero.credit}
        </div>
      )}
      <div className="absolute inset-x-0 bottom-0 z-10 hidden border-t border-border/60 bg-night-deep/70 backdrop-blur-md md:block">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-8 px-8 py-3 font-mono-cinema text-[10px] text-foreground/75">
          <span>{t.hero.tickerNow}</span>
          <span>{t.hero.tickerTime}</span>
          <span>{t.hero.tickerParking}</span>
        </div>
      </div>
    </section>
  );
}
